// Counter Intelligence — pasted-list import helpers.
//
// Users paste tabular item data straight into the chat ("Please import these
// items for me" followed by 30 rows of vendor / description / pack / price)
// instead of uploading a file. These are the pure pieces that make that work:
//
//   detectPastedTableData  — find the table inside a chat message and split it
//                            from the surrounding prose
//   buildPastedSource      — wrap the table as the text source the existing
//                            document-import engine already accepts (the same
//                            shape a .txt/.csv upload produces)
//   extractTemplateDirective — pull a template instruction out of the prose
//                            ("…and make it all into one ordering template")
//                            so it can ride the import → template-setup handoff
//   collapseTableForClassify — shrink the table to a stub for the classifier
//                            call and for conversation history, so routing
//                            stays cheap and the model reads intent, not rows
//
// The drawer (CounterIntelligenceDrawer.jsx) owns the I/O: it feeds the table
// to the same start_discovery → poll → readback pipeline the file upload uses.
// Pure (no React / no DOM / no network) so it is unit-testable.

// The synthetic "file name" a pasted table imports under. Shows up in progress
// notes and nowhere else (the recap deliberately reads "your pasted list").
export const PASTED_SOURCE_NAME = 'Pasted list';

// A chat paste has no file-size dialog in front of it, so cap what we hand the
// extraction engine. 200k chars ≈ a few thousand rows — far beyond any real
// paste, but it stops a runaway paste from tying up the workflow for minutes.
export const MAX_PASTED_TEXT_CHARS = 200000;

// Directives ride localStorage handoffs and an edge-function session row; keep
// them a sentence or three, not a second copy of the message.
export const MAX_TEMPLATE_DIRECTIVE_CHARS = 400;

const stripped = (line) => String(line == null ? '' : line).trim();

// A markdown table's separator row (|---|:--:|) is structure, not data.
const isPipeSeparatorRow = (line) => /^[\s|:\-]+$/.test(line) && line.includes('-');

const countChar = (line, ch) => {
  let n = 0;
  for (let i = 0; i < line.length; i += 1) if (line[i] === ch) n += 1;
  return n;
};

// A pipe row needs at least two pipes (one column boundary can be a stray "a|b"
// in prose; "| a | b |" and "a | b | c" both have ≥2).
const isPipeRow = (line) => countChar(line, '|') >= 2;

// An Excel/Sheets paste separates columns with single tabs, so one tab is
// already a two-column row — but require the split to produce two non-empty
// cells so a line that merely ends in a tab doesn't count.
const isTabRow = (line) => {
  if (!line.includes('\t')) return false;
  return line.split('\t').map(stripped).filter(Boolean).length >= 2;
};

// CSV is the riskiest shape (prose has commas), so a comma row needs ≥2 commas.
// The block-level checks below add consistency + digit requirements on top.
const isCommaRow = (line) => countChar(line, ',') >= 2;

const hasDigit = (line) => /\d/.test(line);

/**
 * Find pasted tabular data inside a chat message.
 *
 * Returns null when the message doesn't contain a convincing table, otherwise:
 *   {
 *     tableText: the table lines, verbatim, in order (what the import reads),
 *     prose:     everything else ("Please import these items for me…"),
 *     rowCount:  data rows — header and markdown separator rows excluded,
 *     delimiter: 'pipe' | 'tab' | 'comma',
 *   }
 *
 * Thresholds are per-delimiter because ambiguity differs: pipes are nearly
 * unambiguous (2 data rows suffice), tabs need 3 rows, and comma tables need 4
 * rows PLUS consistent column counts and digits on most rows, so multi-clause
 * prose ("we need cheese, flour, and sauce, plus…") never reads as a table.
 */
export function detectPastedTableData(text) {
  const raw = String(text || '');
  if (!raw.trim()) return null;
  const lines = raw.split(/\r?\n/);

  const pick = (matcher, minRows) => {
    const matched = [];
    lines.forEach((line, i) => {
      if (matcher(stripped(line))) matched.push(i);
    });
    return matched.length >= minRows ? matched : null;
  };

  let delimiter = null;
  let indexes = pick((l) => isPipeRow(l), 3); // ≥3 pipe lines ≈ header + separator + rows, or 3 data rows
  if (indexes) {
    delimiter = 'pipe';
  } else {
    indexes = pick(isTabRow, 3);
    if (indexes) {
      delimiter = 'tab';
    } else {
      indexes = pick(isCommaRow, 4);
      if (indexes) {
        // Consistency: comma tables have near-uniform column counts…
        const counts = indexes.map((i) => countChar(stripped(lines[i]), ','));
        const min = Math.min(...counts);
        const max = Math.max(...counts);
        // …and item lists carry numbers (prices, pack sizes) on most rows.
        const digitShare = indexes.filter((i) => hasDigit(lines[i])).length / indexes.length;
        if (max - min > 2 || digitShare < 0.5) return null;
        delimiter = 'comma';
      }
    }
  }
  if (!delimiter) return null;

  const indexSet = new Set(indexes);
  const tableLines = indexes.map((i) => lines[i]);
  const proseLines = lines.filter((_, i) => !indexSet.has(i));

  const dataLines = delimiter === 'pipe'
    ? tableLines.filter((l) => !isPipeSeparatorRow(stripped(l)))
    : tableLines;
  // Pipe tables demand ≥2 real data rows once the separator is dropped.
  if (delimiter === 'pipe' && dataLines.length < 2) return null;

  // Header heuristic: a markdown table with a separator always has one; for
  // tab/comma tables, a digit-free first row over digit-bearing rows is one.
  let headerRows = 0;
  if (delimiter === 'pipe' && tableLines.some((l) => isPipeSeparatorRow(stripped(l)))) {
    headerRows = 1;
  } else if (dataLines.length >= 2 && !hasDigit(dataLines[0]) && hasDigit(dataLines[1])) {
    headerRows = 1;
  }

  return {
    tableText: tableLines.join('\n'),
    prose: proseLines.map(stripped).filter(Boolean).join('\n'),
    rowCount: Math.max(dataLines.length - headerRows, 1),
    delimiter,
  };
}

/**
 * The uploaded-source shape the document-import engine already accepts for
 * .txt/.csv files — a pasted table imports through the identical pipeline.
 */
export function buildPastedSource(tableText) {
  return {
    file_name: PASTED_SOURCE_NAME,
    content_type: 'text/plain',
    text: String(tableText || '').slice(0, MAX_PASTED_TEXT_CHARS),
  };
}

// Friendly guard for a paste beyond the engine cap (the engine batches long
// texts fine; this only rejects the truly pathological).
export function validatePastedTable(detection) {
  if (!detection || !detection.tableText) {
    return { ok: false, error: 'I couldn’t find a list of items in that message — paste the rows (one item per line) or upload a file instead.' };
  }
  if (detection.tableText.length > MAX_PASTED_TEXT_CHARS) {
    return { ok: false, error: 'That list is too long to paste in one go — save it as a CSV or spreadsheet and upload the file instead.' };
  }
  return { ok: true };
}

// Wording that marks the prose around a paste as instructions for the
// template step ("make it all into one ordering template", "one order sheet
// per vendor", "organize them by category"). Deliberately template-shaped:
// plain import asks ("please import these items") carry no directive and the
// flow keeps its usual "want templates?" question.
const TEMPLATE_DIRECTIVE_RE = /\b(templates?|order sheets?|ordering|end of period|eop|count sheets?|organi[sz]e)\b/i;

/**
 * Pull a template instruction out of the prose half of a pasted message so it
 * can be carried into the template-setup assistant. Returns the prose (capped)
 * when it contains template wording, else null.
 */
export function extractTemplateDirective(prose) {
  const t = String(prose || '').replace(/\s+/g, ' ').trim();
  if (!t) return null;
  if (!TEMPLATE_DIRECTIVE_RE.test(t)) return null;
  return t.slice(0, MAX_TEMPLATE_DIRECTIVE_CHARS);
}

// How many table lines the classifier still gets to see — enough to recognize
// "this is a vendor/price list", nowhere near enough to bloat the call.
const CLASSIFY_PREVIEW_LINES = 3;

/**
 * The message as the intent classifier should read it: full prose, first few
 * table lines, and an explicit note that N more rows follow. Used both for
 * the live classify call and for this message's entry in later history.
 */
export function collapseTableForClassify(text, detection) {
  if (!detection || !detection.tableText) return String(text || '');
  const tableLines = detection.tableText.split('\n');
  const preview = tableLines.slice(0, CLASSIFY_PREVIEW_LINES);
  const parts = [];
  if (detection.prose) parts.push(detection.prose);
  parts.push(preview.join('\n'));
  if (tableLines.length > preview.length) {
    parts.push(`(…pasted table continues — about ${detection.rowCount} rows of items in total)`);
  }
  return parts.join('\n');
}
